import { computed, getCurrentInstance } from "vue";

import { isNullish } from "@tinyurldev/tu-ui-vue";
import { capitalize, forEach } from "lodash-es";
import { useRouter } from "vue-router/composables";

import {
  getSubscriptionCheckoutSessionApi,
  cancelDowngradeApi,
  reactiveSubscriptionApi,
  updateSubscriptionApi,
} from "@Resources/js/api/subscription";

import useLegacyStore from "@Composables/useLegacyStore";
import useNotify from "@Composables/useNotify";
import usePaymentMethod from "@Composables/usePaymentMethod";
import useTranslation from "@Composables/useTranslation";
import usePaddle from "@Composables/usePaddle";

import BILLING_TYPE from "@Constants/billing-type";
import PADDLE_TRANSACTION_TYPE from "@Constants/paddle-transaction-type";
import {
  isSubscriptionLegacy,
  isSubscriptionLegacyPro,
  isSubscriptionLegacyBulk,
} from "@Helpers/functions/isSubscriptionLegacy";

import { buildUrl, getAppUrl } from "@Helpers/api/buildUrl";
import { logError } from "@Helpers/logger";
import { sleep } from "@Helpers/support";

/**
 * The subscription composable.
 *
 * @returns The composable.
 *
 * @throws {Error} if current instance is not defined.
 */
export default function useSubscription() {
  const router = useRouter();
  const $vm = getCurrentInstance().proxy;
  const { getters } = useLegacyStore();
  const { notifyError } = useNotify();
  const { updatePaymentMethod } = usePaymentMethod();
  const { __ } = useTranslation();
  const { openPaddleCheckout } = usePaddle();

  const currentSubscription = computed(() => getters.getUserCurrentSubscription);
  const userBillingType = computed(() => getters.getUserBillingType);

  if (isNullish($vm)) {
    throw new Error("Current instance is not available.");
  }

  /**
   * Gets the subscription name.
   *
   * @param {Object} subscription The subscription.
   *
   * @returns The subscription name.
   */
  function getSubscriptionName(subscription) {
    return __(`subscriptions:${subscription.name}.title`);
  }

  /**
   * Gets the subscription interval.
   *
   * @param {Object} subscription The subscription.
   *
   * @returns The subscription capitalize interval.
   */
  function getSubscriptionInterval(subscription) {
    const interval = __(`billing.interval.${subscription.interval}`);

    return capitalize(interval);
  }

  /**
   * Gets the subscription name and interval.
   *
   * @param {Object} subscription The subscription.
   *
   * @returns The subscription name and interval with the following format [Name]+[Interval].
   */
  function getSubscriptionNameWithInterval(subscription) {
    const name = getSubscriptionName(subscription);

    if (subscription.name === "free") {
      return name;
    }

    if (!isNullish(subscription.interval)) {
      const interval = getSubscriptionInterval(subscription);

      return `${name} (${capitalize(interval)})`;
    }

    return name;
  }

  /**
   * Handles the "stripe" subscription.
   *
   * @param session The payload session for subscribing.
   */
  async function handleStripeSubscription(session) {
    const stripeResult = await getters.stripe?.redirectToCheckout({
      sessionId: session.session_id,
    });

    if (!isNullish(stripeResult.error)) {
      notifyError(stripeResult.error.message);
    }
  }

  /**
   * Subscribes a user in the given subscription.
   *
   * @param subscription The subscription.
   *
   * @returns The promise that is resolved once the operation is completed.
   */
  async function subscribeUser(subscription) {
    try {
      const sessionResult = await getSubscriptionCheckoutSessionApi(subscription);

      if (!isNullish(sessionResult.error)) {
        notifyError(sessionResult.error.message);
      }

      if (userBillingType.value === BILLING_TYPE.STRIPE) {
        await handleStripeSubscription(sessionResult);
      } else {
        const transactionType = sessionResult.has_unpaid_overage
          ? PADDLE_TRANSACTION_TYPE.SUBSCRIPTION_PURCHASE_WITH_UNPAID_OVERAGE
          : PADDLE_TRANSACTION_TYPE.SUBSCRIPTION_PURCHASE;

        openPaddleCheckout(transactionType, sessionResult);
      }
    } catch (error) {
      logError("Subscription failed: " + error.message);
      notifyError(__("subscriptions.create_failed"));
    }
  }

  /**
   * Subscribes a guest user to the given plan and interval.
   *
   * @param planId   The plan unique identifier.
   * @param interval The plan interval.
   */
  function subscribeGuest(planId, interval, coupon = null) {
    const query = {
      subscription: planId,
      interval,
      ...(coupon && { coupon: coupon }),
    };

    router.push({ name: "register", query: query });
  }

  /**
   * Cancels a schedule to downgrade subscription.
   *
   * @returns The promise that is resolved once the operation is completed.
   */
  async function cancelDowngradeSchedule() {
    try {
      await cancelDowngradeApi();
      await sleep(3000); // FIXME: Temporary fix to bypass realtime behavior.
    } catch (error) {
      logError("Subscription cancellation failed: " + error.message);
    }
  }

  /**
   * Reactivate a cancelled subscription.
   *
   * @returns {Promise} A promise that resolves when the subscription is reactivated.
   */
  async function reactivateSubscription() {
    try {
      await reactiveSubscriptionApi();
      await sleep(3000); // FIXME: Temporary fix to bypass realtime behavior.
    } catch (error) {
      logError("Subscription reactivation failed: " + error.message);
    }
  }

  /**
   * Changes the current subscription to a new plan.
   *
   * @param subscription The target subscription name.
   * @param interval The target subscription interval.
   * @param currency The target subscription currency.
   * @param type The target subscription type.
   * @param billing_type The billing type.
   * @param coupon The coupon code.
   */
  async function updateSubscription(subscription, interval, billing_type, currency, type, coupon) {
    try {
      await updateSubscriptionApi(subscription, interval, billing_type, currency, type, coupon);
    } catch (error) {
      logError("Subscription update failed: " + error.message);

      switch (error.response?.status) {
        case 402:
          if (userBillingType.value === BILLING_TYPE.STRIPE) {
            const urlForPayment = "/{cashierPath}/payment/{paymentId}?redirect={urlForPlanRedirect}";
            const urlForPlanRedirect = URI(getAppUrl("settings/subscription")).absoluteTo(window.location).toString();

            window.location = buildUrl(urlForPayment, {
              cashierPath: $vm.$Spark("cashierPath"),
              paymentId: error.response.data.paymentId,
              urlForPlanRedirect: urlForPlanRedirect,
            });
          } else {
            notifyError(__("subscriptions.payment_declined"));
            updatePaymentMethod(currentSubscription.value);
          }
          break;

        case 406:
          notifyError(error.response.data.message);
          break;

        case 422:
          forEach(error.response.data.errors, (errors) => {
            forEach(errors, (error) => {
              notifyError(error);
            });
          });
          break;

        default:
          notifyError(__("subscriptions.update_failed"));
      }

      throw error;
    }
  }

  return {
    getSubscriptionName,
    getSubscriptionInterval,
    getSubscriptionNameWithInterval,
    handleStripeSubscription,
    isSubscriptionLegacy,
    isSubscriptionLegacyPro,
    isSubscriptionLegacyBulk,
    cancelDowngradeSchedule,
    reactivateSubscription,
    updateSubscription,
    subscribeUser,
    subscribeGuest,
  };
}
